import { useState } from "react";
import { Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getTimeZones, timeZonesNames } from "@vvo/tzdb";

// Fonction helper pour formater les timezones
function formatTimezone(timezone: string): string {
  const timezones = getTimeZones();
  const tz = timezones.find((t) => t.name === timezone);

  if (!tz) return timezone;

  // Formater comme "Europe/Paris (UTC+01)"
  const offset = tz.currentTimeOffsetInMinutes / 60;
  const sign = offset >= 0 ? "+" : "-";
  const hours = Math.abs(offset).toString().padStart(2, "0");

  return `${timezone} (UTC${sign}${hours})`;
}

// Fonction helper pour grouper les timezones par région
function groupTimezonesByRegion() {
  const regions: Record<string, string[]> = {};

  timeZonesNames.forEach((timezone) => {
    const parts = timezone.split("/");
    const region = parts[0];

    if (!regions[region]) {
      regions[region] = [];
    }
    regions[region].push(timezone);
  });

  // Trier les régions et les timezones dans chaque région
  const sortedRegions: Record<string, string[]> = {};
  Object.keys(regions)
    .sort()
    .forEach((region) => {
      sortedRegions[region] = regions[region].sort();
    });

  return sortedRegions;
}

// Obtenir le fuseau horaire par défaut
function getDefaultTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "Europe/Paris"; // Fallback si l'API Intl n'est pas disponible
  }
}

interface TimezoneDisplayProps {
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
  showPlaceholder?: boolean; // Nouveau prop pour afficher le placeholder au lieu de la valeur
  hasError?: boolean; // Nouveau prop pour indiquer s'il y a une erreur
  isSubmitted?: boolean; // Pour savoir si le formulaire a été soumis
}

export function TimezoneDisplay({ value, onValueChange, className, showPlaceholder = false, hasError = false, isSubmitted = false }: TimezoneDisplayProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [touched, setTouched] = useState(false);
  const groupedTimezones = groupTimezonesByRegion();
  const defaultTimezone = getDefaultTimezone();

  // Fonction helper pour déterminer si on doit afficher l'erreur (rouge)
  const shouldShowError = () => {
    if (isSubmitted) return hasError; // Toujours afficher après submit si erreur
    if (!touched) return false; // Ne pas afficher si pas encore touché
    return hasError; // Afficher seulement si touché ET qu'il y a une erreur
  };

  const handleValueChange = (newValue: string) => {
    // Si c'est le placeholder, ne rien faire ou remettre à vide
    if (newValue === "placeholder") {
      onValueChange("");
      setIsEditing(false);
      return;
    }
    onValueChange(newValue);
    setIsEditing(false);
  };

  const handleClick = () => {
    setTouched(true); // Marquer comme touché quand l'utilisateur clique
    setIsEditing(true);
  };

  // Utiliser la valeur du form ou fallback sur la timezone par défaut
  const effectiveValue = value || defaultTimezone;

  // Afficher le placeholder seulement si explicitement demandé ET qu'il n'y a pas de valeur
  const shouldShowPlaceholder = showPlaceholder && !value;

  if (isEditing) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <Select
          value={value || "placeholder"} // Utiliser "placeholder" si pas de valeur
          onValueChange={handleValueChange}
          onOpenChange={(open) => {
            if (!open) {
              setIsEditing(false);
            }
          }}
          open={true}
        >
          <SelectTrigger className="h-8 w-auto min-w-[200px] text-xs">
            <SelectValue placeholder="Sélectionnez un fuseau horaire" />
          </SelectTrigger>
          <SelectContent className="max-h-[400px]">
            {/* Groupe spécial avec placeholder et timezone par défaut */}
            <SelectGroup>
              <SelectLabel className="bg-accent text-xs text-muted-foreground">Sélection</SelectLabel>
              {/* Timezone par défaut en deuxième position */}
              <SelectItem value={defaultTimezone} className="text-xs font-medium">
                {formatTimezone(defaultTimezone)} <span className="font-normal text-muted-foreground italic">défaut</span>
              </SelectItem>
            </SelectGroup>

            {/* Tous les groupes de timezones normaux */}
            {Object.entries(groupedTimezones).map(([region, timezones]) => (
              <SelectGroup key={region}>
                <SelectLabel className="bg-accent text-xs text-muted-foreground">{region}</SelectLabel>
                {timezones.map((timezone) => (
                  <SelectItem key={timezone} value={timezone} className="text-xs">
                    {timezone}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Button
        variant="ghost"
        size="sm"
        className={cn(
          "flex h-auto content-center items-center p-0 text-xs text-muted-foreground hover:text-foreground",
          shouldShowError() && shouldShowPlaceholder && "text-red-500 hover:text-red-600"
        )}
        onClick={handleClick}
      >
        <span className="mr-1">{shouldShowPlaceholder ? "Sélectionnez votre fuseau horaire" : formatTimezone(effectiveValue)}</span>
        <Clock className={cn("h-3 w-3 shrink-0 text-muted-foreground", shouldShowError() && shouldShowPlaceholder && "text-red-500")} />
      </Button>
    </div>
  );
}
